import React from "react";
import {Button, Grid2, Typography} from "@mui/material";
import BackgroundSvg from "../assets/background.svg?react";
import BoxHubSvg from "../assets/boxhub.svg?react";

import {color_blue, color_green, color_grey} from "@/agentur/AgenturColors";
import {scrollToNextSection} from "@/lib/utils";
import {compactValue} from "@/lib/screen-class";

import Box from "@mui/material/Box";

import {ScrollingLogoMarquee} from "@/pages/landing-page/utils/ScrollingLogoMarquee";
import {Section1DTO} from "@/pages/landing-page/landing-page-data";
import {BoxesSvg, CubeSvg} from "@/pages/landing-page/utils/Svgs";

interface Section1Props {
    data?: Section1DTO;
    contactRef: React.RefObject<any>;
}

const BRAND_PLACEHOLDER = "{brand}";

/**
 * Renders an intro paragraph while preserving the shared "BoxHub" brand highlight (bold "Box").
 * The `{brand}` placeholder in the localized copy marks where the brand name is rendered inline.
 */
const IntroText = ({ intro }: { intro: string }) => {
    const parts = intro.split(BRAND_PLACEHOLDER);
    return (
        <>
            {parts.map((part, index) => (
                <React.Fragment key={`${index}:${part}`}>
                    {index > 0 && (
                        <>
                            <strong>Box</strong>Hub
                        </>
                    )}
                    {part}
                </React.Fragment>
            ))}
        </>
    );
};

export const Section1 = ({ contactRef, data }: Section1Props) => (
    <Grid2
        container
        direction="column"
        sx={{
            position: "relative",
            width: "100%",
            minHeight: "100%",
            paddingTop: {
                xs: 4,
                lg: 12
            }
        }}
    >
        <BackgroundSvg
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: 0,
                transform: "translateZ(0)",
                backgroundColor: color_grey[3]
            }}
            // width="100%"
            height="100%"
        />
        <Box
            sx={{
                position: "absolute",
                top: 40,
                right: {
                    xs: 12,
                    lg: 120
                },
                zIndex: 1,
                // Below `sm` the logo takes the full width and the decoration would lie on top of it.
                display: { xs: "none", sm: "block" }
            }}
        >
            {/* Decoration behind the logo and the title (`zIndex` 1 against 2), smaller below `md`. */}
            <Box
                component={BoxesSvg}
                style={{
                    zIndex: 1,
                    transform: "translateZ(0)",
                    backgroundColor: color_grey[3]
                }}
                sx={{ width: compactValue("64px", "120px") }}
            />
        </Box>

        <Grid2
            container
            direction="column"
            gap={1}
            sx={{
                width: "100%",
                zIndex: 2,
                // The grey background reaches the page edge, so the text keeps a distance to it.
                paddingLeft: {
                    xs: 2,
                    md: 3,
                    lg: 12
                },
                paddingRight: {
                    xs: 2,
                    md: 3,
                    lg: 0
                }
            }}
        >
            {/* Below `md` the logo scales with the width up to its own 498 px: a `transform` would shrink only the
                drawing, not the space it takes, and the logo would stick out of a narrow window. */}
            <Grid2
                data-cy="landing-logo"
                sx={{
                    "& svg": {
                        width: compactValue("100%", "498px"),
                        maxWidth: compactValue("498px", "none"),
                        height: compactValue("auto", "84px"),
                        transform: compactValue("none", "scale(1.2)"),
                        transformOrigin: "left center"
                    }
                }}
            >
                <BoxHubSvg />
            </Grid2>
            <Grid2 width="100%">
                {/* Below `md` the title takes the size of the `h2` variant and stays an `h1`. Only the size keys are
                    switched: a responsive `typography` key would also bring back the variant's colour and weight. */}
                <Typography
                    variant="h1"
                    color={color_green[2]}
                    fontWeight={700}
                    marginLeft="-5px"
                    sx={(theme) => ({
                        fontSize: compactValue(theme.typography.h2.fontSize, theme.typography.h1.fontSize),
                        lineHeight: compactValue(theme.typography.h2.lineHeight, theme.typography.h1.lineHeight),
                        letterSpacing: compactValue(
                            theme.typography.h2.letterSpacing,
                            theme.typography.h1.letterSpacing
                        ),
                        overflowWrap: "break-word"
                    })}
                >
                    {data?.title}
                </Typography>
            </Grid2>
            <Grid2
                container
                direction="row"
                width="100%"
                sx={{
                    gap: {
                        xs: 4,
                        lg: 12
                    }
                }}
            >
                <Grid2 container direction="column" gap={4} size={{ xs: 12, md: 6, lg: 7, xl: 7 }}>
                    <Grid2>
                        <Typography
                            variant="h4"
                            sx={(theme) => ({
                                fontSize: compactValue(theme.typography.h5.fontSize, theme.typography.h4.fontSize),
                                lineHeight: compactValue(theme.typography.h5.lineHeight, theme.typography.h4.lineHeight),
                                color: "#000080",
                                fontWeight: 500,
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                paddingTop: {
                                    xs: 2,
                                    lg: 10
                                }
                            })}
                        >
                            {data?.subtitle}
                        </Typography>
                    </Grid2>
                    <Grid2>
                        <Typography
                            sx={{
                                maxWidth: "700px",
                                color: "#000080",
                                fontSize: compactValue("105%", "120%"),
                                lineHeight: compactValue(1.6, 1.8)
                            }}
                        >
                            {data?.intro && <IntroText intro={data.intro} />}
                        </Typography>
                    </Grid2>
                    <Grid2>
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: color_green[2],
                                color: "#000080",
                                fontWeight: "bold",
                                padding: "8px 24px"
                            }}
                            onClick={() => scrollToNextSection(contactRef)}
                        >
                            {data?.cta}
                        </Button>
                    </Grid2>
                </Grid2>
                <Grid2
                    size={{ xs: 12, md: 5, lg: 4, xl: 4 }}
                    container
                    direction="column"
                    sx={{
                        zIndex: 2,
                        transform: "translateZ(0)",
                        gap: {
                            xs: 1,
                            md: 2,
                            lg: 4
                        },
                        paddingTop: {
                            xs: 0,
                            md: 12,
                            lg: 20
                        },
                        paddingBottom: {
                            xs: 2,
                            lg: 6
                        }
                    }}
                    // width="30rem"
                >
                    {data?.bulletPoints?.map((text) => (
                        <Grid2
                            key={text}
                            container
                            sx={{
                                gap: {
                                    xs: 2,
                                    lg: 4
                                }
                            }}
                            flexWrap="nowrap"
                        >
                            <Grid2>
                                <CubeSvg />
                            </Grid2>
                            <Grid2 size={{ xs: 12, lg: 5 }}>
                                <Typography variant="body1" fontWeight="bold" lineHeight="1.2">
                                    {text}
                                </Typography>
                            </Grid2>
                        </Grid2>
                    ))}
                </Grid2>
            </Grid2>
        </Grid2>
        <Grid2 flexGrow={1} />
        <Grid2 flexShrink={0}>
            <ScrollingLogoMarquee
                logos={data?.logos.map((logo) => (
                    <img key={logo.alt} src={logo.image} alt={logo.alt} />
                ))}
                speed={20}
                gap={40}
                backgroundColor={color_blue[2]}
            />
        </Grid2>
    </Grid2>
);
